import { useCallback, useEffect, useState, type FormEvent } from 'react'
import {
  normalizeCharacterCode,
  tokenFromFragment,
  type CharacterSheet,
  type CharacterSummary,
  type InvitePreview,
  type JoinCharacterOrigin,
  type JoinCharacterRef,
  type RequestJoinResult,
  type SeatState,
} from '@rollcall/contracts'
import { CreationRefused, type Api } from '../api.js'
import { useT } from './i18n-context.js'
import { LevelUp } from './level-up.js'
import { CreationWizard } from './wizard.js'

/**
 * Приход игрока по ссылке кампании (D51).
 *
 * Ссылка одна на всю кампанию и работает сколько угодно раз: мастер
 * отправил её в общий чат, и по ней приходят все — по одному, с
 * разницей в дни. Место при переходе не занимается: игрок приносит
 * персонажа, заявка встаёт в очередь, и место появляется, когда
 * мастер подтвердит.
 *
 * Игрок здесь пользователем не является: аккаунта у него нет, и всё,
 * что его удостоверяет, — токен устройства в заголовке (D29).
 *
 * Что видно до подтверждения: название кампании и сколько осталось
 * мест. Названия достаточно, чтобы человек понял, туда ли он попал,
 * прежде чем строить персонажа, и оно всё равно стоит в том же
 * сообщении чата. Состава партии и чужих персонажей не видно —
 * это по-прежнему закрыто (D38).
 *
 * Пока заявка ждёт решения, экран сам спрашивает сервер, не решилось
 * ли. Не потоком событий (D2): поток кампании открыт мастеру
 * и занявшим места, а тот, кто только постучался, участником ещё
 * не является. Опрос кончается вместе с ожиданием.
 */

/**
 * Как часто спрашивать, решилась ли заявка.
 *
 * Пять секунд: человек в этот момент смотрит на экран и ждёт, а запрос
 * один и дешёвый. Опрос идёт, только пока есть чего ждать.
 */
export const WAIT_POLL_MS = 5_000

function Refusal({ error, fallback }: { error: unknown; fallback: string }) {
  if (error === null || error === undefined) return null
  const text = error instanceof Error ? error.message : fallback
  return (
    <p className="app-problems" role="alert">
      {text}
    </p>
  )
}

export function JoinScreen({
  api,
  hash,
  onOpenCharacter,
  onCreateCharacter,
}: {
  readonly api: Api
  readonly hash: string
  readonly onOpenCharacter: (characterId: string) => void
  /**
   * Завести персонажа сразу за место. Нужно только кампаниям,
   * заведённым до D51: там место создавали заранее, и оно может
   * стоять пустым. По общей ссылке персонаж создаётся здесь же,
   * до всякого места.
   */
  readonly onCreateCharacter: (seatId: string, startLevel: number) => void
}) {
  const t = useT()
  const token = tokenFromFragment(hash)
  const [preview, setPreview] = useState<InvitePreview | null>(null)
  const [state, setState] = useState<'working' | 'ready' | 'refused'>(
    // Без токена это просто заход на свои места: проверять нечего.
    token === null ? 'ready' : 'working',
  )
  const [sent, setSent] = useState<RequestJoinResult | null>(null)
  const [seats, setSeats] = useState<SeatState[]>([])
  const [error, setError] = useState<unknown>(null)

  const reloadSeats = useCallback(() => {
    void api
      .mySeats()
      .then(setSeats)
      .catch(() => {
        // Список мест не открылся — экран приглашения от этого
        // не ломается: заявка уже подана.
      })
  }, [api])

  /**
   * Ожидание решения: опрос, пока есть незакрытая заявка.
   *
   * Признак ожидания — место в состоянии `pending` среди своих:
   * как только мастер решил, опрос прекращается сам.
   */
  const waiting = seats.some((place) => place.status === 'pending')

  useEffect(() => {
    if (!waiting) return
    const timer = setInterval(reloadSeats, WAIT_POLL_MS)
    return () => {
      clearInterval(timer)
    }
  }, [waiting, reloadSeats])

  useEffect(() => {
    reloadSeats()
    if (token === null) return
    let cancelled = false
    void api
      .invitePreview(token)
      .then((next) => {
        if (cancelled) return
        setPreview(next)
        setState('ready')
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        setError(cause)
        setState('refused')
      })
    return () => {
      cancelled = true
    }
  }, [api, token, reloadSeats])

  const submit = (character: JoinCharacterRef, origin: JoinCharacterOrigin) => {
    if (token === null) return
    setError(null)
    void api
      .requestJoin(token, character, origin)
      .then((result) => {
        setSent(result)
        reloadSeats()
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
  }

  const full = preview !== null && preview.seatsUsed >= preview.seatLimit

  return (
    <section className="app-screen app-auth">
      <h2>{t('join.title')}</h2>
      {state === 'working' && <p>{t('join.working')}</p>}

      {state === 'refused' && (
        <>
          <p className="app-problems" role="alert">
            {t('join.refused')}
          </p>
          {/* Своё место с нового устройства возвращают по коду
              персонажа, а не по ссылке (D38). Это не второй вход
              в кампанию: сюда приходит тот, кто уже за столом. */}
          <DeviceChangeForm api={api} onSent={reloadSeats} />
        </>
      )}

      {preview !== null && (
        <>
          <h3>{preview.title}</h3>
          <p className="app-value-note">
            {t('join.seatsLeft', {
              left: Math.max(0, preview.seatLimit - preview.seatsUsed),
            })}
          </p>
        </>
      )}

      {sent === null && preview !== null && (
        // Мест не осталось — заявку всё равно можно подать: она
        // отклонится сама, но мастер её увидит и поднимет предел
        // (D51). Молчаливая заглушка вместо этого оставила бы
        // человека без хода вовсе.
        <>
          {full && <p className="app-value-note">{t('join.full')}</p>}
          <PickCharacter api={api} startLevel={preview.startLevel} onPick={submit} />
        </>
      )}

      {sent !== null && sent.status === 'pending' && (
        <p className="app-auth-notice">{t('join.pending')}</p>
      )}

      {sent !== null && sent.status === 'rejected' && (
        // Отказ по пределу мест — не ошибка: человек мог перейти
        // по чужой ссылке без злого умысла, и виноватым он себя
        // чувствовать не должен (D51).
        <div className="app-auth-notice">
          <p>{t(`join.rejected.${sent.reason ?? 'seat_limit'}`)}</p>
          <p className="app-field-hint">{t('join.rejectedHint')}</p>
        </div>
      )}

      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={() => {
            setSent(null)
            setError(null)
            reloadSeats()
          }}
        >
          {t('join.recheck')}
        </button>
      </div>

      <MySeats
        api={api}
        seats={seats}
        onChanged={reloadSeats}
        onOpenCharacter={onOpenCharacter}
        onCreateCharacter={onCreateCharacter}
      />
      <Refusal error={state === 'refused' ? null : error} fallback={t('join.failed')} />
    </section>
  )
}

/**
 * Кого отправить в заявку: готового персонажа или нового.
 *
 * Оба пути ведут в одну заявку и различаются только пометкой
 * `origin` — она подсказывает мастеру, кого он видит, и ничего
 * не решает (D51). Визард стоит прямо здесь, а не отдельным
 * экраном: уйти на него значило бы унести токен из адреса и
 * потерять ссылку на середине создания.
 */
function PickCharacter({
  api,
  startLevel,
  onPick,
}: {
  readonly api: Api
  /** С какого уровня кампания пускает за стол (D60): умолчание сборки. */
  readonly startLevel: number
  readonly onPick: (character: JoinCharacterRef, origin: JoinCharacterOrigin) => void
}) {
  const t = useT()
  const [drafts, setDrafts] = useState<readonly CharacterSummary[]>([])
  const [creating, setCreating] = useState(false)
  // Сборка до цели (D60): персонаж создан первым уровнем, и до заявки
  // игрок проходит экраны повышения здесь же — уйти на лист значило
  // бы унести токен из адреса и потерять ссылку.
  const [building, setBuilding] = useState<{
    readonly characterId: string
    readonly target: number
  } | null>(null)
  const [code, setCode] = useState('')

  useEffect(() => {
    void api
      .listCharacters(null)
      .then(setDrafts)
      .catch(() => {
        // Своих персонажей нет или список не пришёл — остаётся
        // создать нового, и эта кнопка на месте.
      })
  }, [api])

  if (building !== null) {
    return (
      <JoinBuild
        api={api}
        characterId={building.characterId}
        target={building.target}
        onBuilt={() => {
          onPick({ kind: 'mine', characterId: building.characterId }, 'new')
        }}
        onStop={() => {
          // Прервал — персонаж остался на достигнутом уровне. Он уже
          // в списке своих: отправить его как есть или добрать позже
          // решает игрок, а не мы за него.
          setBuilding(null)
          setCreating(false)
          void api
            .listCharacters(null)
            .then(setDrafts)
            .catch(() => {})
        }}
      />
    )
  }

  if (creating) {
    return (
      <CreationWizard
        api={api}
        // Места ещё нет: персонаж создаётся драфтом этого устройства
        // и садится за стол, только когда мастер подтвердит (D51).
        seatId={null}
        campaignLevel={startLevel}
        onCreated={(character, target) => {
          if (target > 1) {
            setBuilding({ characterId: character.id, target })
            return
          }
          onPick({ kind: 'mine', characterId: character.id }, 'new')
        }}
        onCancel={() => {
          setCreating(false)
        }}
      />
    )
  }

  /**
   * Свой персонаж по коду (D55).
   *
   * Заявка уходит на сервер, а не ищется среди своих: смысл кода
   * ровно в том, что персонаж остался на другом устройстве, и здесь
   * его нет. Раньше форма искала код в списке выше — то есть среди
   * тех, кого и так видно, — и молча ничего не делала.
   */
  const byCode = (event: FormEvent) => {
    event.preventDefault()
    onPick({ kind: 'code', code: normalizeCharacterCode(code) }, 'draft')
  }

  return (
    <>
      <h4>{t('join.pickCharacter')}</h4>
      <p className="app-field-hint">{t('join.pickCharacterHint')}</p>
      {drafts.length > 0 && (
        <ul className="app-list">
          {drafts.map((draft) => (
            <li key={draft.id} className="app-card">
              <button
                type="button"
                className="app-button"
                onClick={() => {
                  onPick({ kind: 'mine', characterId: draft.id }, 'draft')
                }}
              >
                {draft.name}
              </button>
              <p className="app-value-note">{draft.code}</p>
            </li>
          ))}
        </ul>
      )}
      <div className="app-actions">
        <button
          type="button"
          className="app-button"
          onClick={() => {
            setCreating(true)
          }}
        >
          {t('join.newCharacter')}
        </button>
      </div>
      <form className="app-form" onSubmit={byCode}>
        <h4>{t('join.attach')}</h4>
        <p className="app-field-hint">{t('join.attachHint')}</p>
        <label className="app-field">
          <span className="app-field-label">{t('join.code')}</span>
          <input
            required
            value={code}
            onChange={(event) => {
              setCode(event.target.value)
            }}
          />
        </label>
        <button type="submit" className="app-button app-button-quiet">
          {t('join.attachSubmit')}
        </button>
      </form>
    </>
  )
}

/**
 * Заявка на возврат за своё место с нового устройства (D38).
 *
 * По коду персонажа: он уже сидит за местом, и общая ссылка кампании
 * его туда не вернёт — по ней приходят с новым персонажем. Ответ
 * одинаков независимо от того, нашёлся ли персонаж, — иначе код,
 * введённый наугад, подтверждал бы, что такой есть.
 */
export function DeviceChangeForm({
  api,
  onSent,
}: {
  readonly api: Api
  readonly onSent: () => void
}) {
  const t = useT()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    void api
      .requestDeviceChange(normalizeCharacterCode(code))
      .then(() => {
        setSent(true)
        onSent()
      })
      .catch((cause: unknown) => {
        setError(cause)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  if (sent) return <p className="app-auth-notice">{t('join.pending')}</p>

  return (
    <form className="app-form" onSubmit={submit}>
      <h3>{t('join.deviceChange')}</h3>
      <p className="app-field-hint">{t('join.deviceChangeHint')}</p>
      <label className="app-field">
        <span className="app-field-label">{t('join.code')}</span>
        <input
          required
          value={code}
          onChange={(event) => {
            setCode(event.target.value)
          }}
        />
      </label>
      <Refusal error={error} fallback={t('join.failed')} />
      <button type="submit" className="app-button" disabled={busy}>
        {t('join.deviceChangeSubmit')}
      </button>
    </form>
  )
}

/** Места этого устройства: и те, что ждут решения, и занятые. */
export function MySeats({
  api,
  seats,
  onChanged,
  onOpenCharacter,
  onCreateCharacter,
}: {
  readonly api: Api
  readonly seats: readonly SeatState[]
  readonly onChanged: () => void
  readonly onOpenCharacter: (characterId: string) => void
  readonly onCreateCharacter: (seatId: string, startLevel: number) => void
}) {
  const t = useT()
  if (seats.length === 0) return null

  return (
    <>
      <h3>{t('join.mySeats')}</h3>
      <ul className="app-list">
        {seats.map((seat, index) =>
          seat.status === 'taken' ? (
            <li key={seat.seatId} className="app-card">
              <h4>
                {t('join.seatOf', {
                  campaign: seat.campaignTitle,
                  seat: seat.seatLabel,
                })}
              </h4>
              {seat.archived && <p className="app-value-note">{t('join.archived')}</p>}
              {seat.character === null ? (
                // Место без персонажа остаётся у кампаний, заведённых
                // до D51: там место создавали заранее, а персонажа
                // за него сажали отдельно (D29).
                <TakeSeat
                  api={api}
                  seatId={seat.seatId}
                  onAttached={onChanged}
                  onCreate={() => {
                    onCreateCharacter(seat.seatId, seat.startLevel)
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="app-button"
                  onClick={() => {
                    if (seat.character !== null) onOpenCharacter(seat.character.id)
                  }}
                >
                  {seat.character.name}
                </button>
              )}
            </li>
          ) : (
            // Заявка ещё не решена: о кампании по-прежнему ничего
            // не известно, и показывать нечего, кроме ожидания (D38).
            <li key={`${seat.status}-${index}`} className="app-card">
              <p>
                {seat.status === 'pending'
                  ? t('join.pending')
                  : t('join.rejected.byGm')}
              </p>
              {/* Что чинить: отказ без причины оставляет человека
                  гадать — уровень, запись, которой нет, или вовсе
                  ничего (D61). */}
              {seat.status === 'rejected' && seat.note !== null && (
                <p className="app-field-hint" data-testid="reject-note">
                  {seat.note}
                </p>
              )}
            </li>
          ),
        )}
      </ul>
    </>
  )
}

/** Подбор своего драфта за уже созданное место по коду (D29). */
function TakeSeat({
  api,
  seatId,
  onAttached,
  onCreate,
}: {
  readonly api: Api
  readonly seatId: string
  readonly onAttached: () => void
  readonly onCreate: () => void
}) {
  const t = useT()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    void api
      .attachCharacter(seatId, normalizeCharacterCode(code))
      .then(onAttached)
      .catch((cause: unknown) => {
        setError(cause)
      })
      .finally(() => {
        setBusy(false)
      })
  }

  return (
    <>
      <p>{t('join.needCharacter')}</p>
      <form className="app-form" onSubmit={submit}>
        <h4>{t('join.attach')}</h4>
        <p className="app-field-hint">{t('join.attachHint')}</p>
        <label className="app-field">
          <span className="app-field-label">{t('join.code')}</span>
          <input
            required
            value={code}
            onChange={(event) => {
              setCode(event.target.value)
            }}
          />
        </label>
        <Refusal error={error} fallback={t('join.failed')} />
        <button type="submit" className="app-button" disabled={busy}>
          {t('join.attachSubmit')}
        </button>
      </form>
      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          onClick={onCreate}
        >
          {t('join.newCharacter')}
        </button>
      </div>
    </>
  )
}

/**
 * Экраны повышения подряд до цели сборки, прямо в заявке (D60).
 *
 * Тот же экран, что на листе: персонаж третьего уровня — это три шага
 * журнала, и второго способа их собрать нет. Дошли до цели — заявка
 * уходит сама; прервали — персонаж остаётся на достигнутом уровне.
 */
export function JoinBuild({
  api,
  characterId,
  target,
  onBuilt,
  onStop,
}: {
  readonly api: Api
  readonly characterId: string
  readonly target: number
  readonly onBuilt: () => void
  readonly onStop: () => void
}) {
  const t = useT()
  const [sheet, setSheet] = useState<CharacterSheet | null>(null)
  const [busy, setBusy] = useState(false)
  const [problems, setProblems] = useState<Readonly<Record<string, readonly string[]>>>(
    {},
  )
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    void api
      .characterSheet(characterId)
      .then(setSheet)
      .catch(() => {
        setFailure(t('errors.network'))
      })
  }, [api, characterId, t])

  if (failure !== null) return <p className="app-problems">{failure}</p>
  if (sheet === null) return <p>{t('wizard.loading')}</p>

  return (
    <>
      <p className="app-notice" role="status" data-testid="build-progress">
        {t('level.buildLeft', { target, count: target - sheet.level })}
      </p>
      <LevelUp
        key={sheet.level}
        api={api}
        sheet={sheet}
        closeLabel={t('level.stopBuild')}
        busy={busy}
        problems={problems}
        onLevelUp={(input) => {
          setBusy(true)
          setProblems({})
          void api
            .levelUp({ characterId, ...input })
            .then((next) => {
              if (next.level >= target) {
                onBuilt()
                return
              }
              setSheet(next)
            })
            .catch((error: unknown) => {
              if (error instanceof CreationRefused) {
                const grouped: Record<string, string[]> = {}
                for (const problem of error.problems) {
                  grouped[problem.slot] = [
                    ...(grouped[problem.slot] ?? []),
                    problem.message,
                  ]
                }
                setProblems(grouped)
                return
              }
              setFailure(t('errors.unknown'))
            })
            .finally(() => {
              setBusy(false)
            })
        }}
        onLevelDown={() => {
          setBusy(true)
          void api
            .levelDown(characterId)
            .then(setSheet)
            .catch(() => {
              setFailure(t('errors.unknown'))
            })
            .finally(() => {
              setBusy(false)
            })
        }}
        onClose={onStop}
      />
    </>
  )
}
